/**
 * What happens to a picture when it is added - all of it here, on the device.
 * The photo itself never leaves it (2026-10-10; until then the server did this).
 *
 * The browser decodes the photo (turned upright by its EXIF orientation - every
 * current browser does that for <img>), and draws it smaller onto a white
 * background as JPEG copies: a thumbnail for lists and a preview for the editor,
 * which is also what the PDF is made from. Drawing onto a canvas keeps none of
 * the photo's hidden details: no GPS position, no camera, no date. The original
 * file is not kept.
 */
import type { Size } from '../api.ts';

export const SIZES = {
  thumb: 400,      // lists and pickers
  // the editor and the PDF: the largest zone is ~125 mm, so 1600 px is over 300 dpi on it
  preview: 1600,
} as const;
export type SizeKind = keyof typeof SIZES;

export interface PreparedFile { kind: SizeKind; blob: Blob; width: number; height: number }

export class PictureError extends Error {}

/** Why a picture cannot be used, in words for the person who picked it. */
export const PICTURE_PROBLEM = {
  heic: 'This browser cannot open iPhone HEIC pictures. Share it as a JPEG (on iPhone: Settings → Camera → Formats → Most Compatible), or open FoldMyCard in Safari.',
  unreadable: 'That file is not a picture this browser can open. Please use a JPEG, PNG or WebP.',
  tooSmall: 'That picture is too small to print well. Please use one at least 200 pixels on each side.',
};
/** The smallest picture worth printing, on its shorter side. */
export const MIN_PICTURE_PX = 200;
export const isHeic = (file: File) => /\.hei[cf]$/i.test(file.name) || /hei[cf]/i.test(file.type);
/**
 * What the file picker offers: every picture a browser may open - HEIC too, so an iPhone
 * photo is not hidden from the list (Safari opens it; elsewhere the person is told why not).
 */
export const PICTURE_ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/gif,image/heic,image/heif,.heic,.heif';

/**
 * Decode a picture file with the browser's own decoder, upright. Loaded, not
 * `decode()`d: a browser may hold decoding back while the page is out of sight,
 * and the person who switched tabs while a picture was being added would come
 * back to it still waiting (2026-10-11); drawing it decodes it anyway.
 */
export async function decode(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new PictureError('This picture could not be read.'));
      img.src = url;
    });
    return img;
  } finally {
    // a decoded image keeps its pixels; the address is no longer needed
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Draw `src` (w x h) at `width` x `height` on white. Big reductions go in halves,
 * because a browser shrinking a photo a long way in one step leaves it jagged.
 */
export function drawScaled(src: CanvasImageSource, w: number, h: number, width: number, height: number): HTMLCanvasElement {
  let cur = src, cw = w, ch = h;
  const temps: HTMLCanvasElement[] = [];
  while (cw / 2 >= width && ch / 2 >= height) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(cw / 2));
    c.height = Math.max(1, Math.round(ch / 2));
    const g = c.getContext('2d')!;
    g.imageSmoothingQuality = 'high';
    g.drawImage(cur, 0, 0, c.width, c.height);
    temps.push(c);
    cur = c; cw = c.width; ch = c.height;
  }
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  const g = out.getContext('2d')!;
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, width, height);
  g.imageSmoothingQuality = 'high';
  g.drawImage(cur, 0, 0, width, height);
  for (const c of temps) { c.width = 0; c.height = 0; }      // phones have little canvas memory: give it back now
  return out;
}

export function toJpeg(c: HTMLCanvasElement, quality = 0.88): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob(
    (b) => (b ? resolve(b) : reject(new PictureError('This browser could not save the picture.'))), 'image/jpeg', quality));
}

/** The size to draw at: `long` on the longer side at most, never larger than it was. */
export function fit(w: number, h: number, long: number): Size {
  const k = Math.min(1, long / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}

/** Every size of a picture, from the file the person picked. */
export async function preparePicture(file: File): Promise<{ width: number; height: number; files: PreparedFile[] }> {
  let img: HTMLImageElement;
  try {
    img = await decode(file);
  } catch {
    throw new PictureError(isHeic(file) ? PICTURE_PROBLEM.heic : PICTURE_PROBLEM.unreadable);
  }
  const w = img.naturalWidth, h = img.naturalHeight;
  if (!w || !h) throw new PictureError('That picture has no size.');
  if (w < MIN_PICTURE_PX || h < MIN_PICTURE_PX) throw new PictureError(PICTURE_PROBLEM.tooSmall);
  const files: PreparedFile[] = [];
  // largest first, then each smaller one from the one before: less work, and less memory on a phone
  let src: CanvasImageSource = img, sw = w, sh = h;
  for (const kind of ['preview', 'thumb'] as SizeKind[]) {
    const s = fit(w, h, SIZES[kind]);
    const c = drawScaled(src, sw, sh, s.width, s.height);
    files.push({ kind, blob: await toJpeg(c), ...s });
    src = c; sw = s.width; sh = s.height;
  }
  return { width: w, height: h, files: files.reverse() };
}

/**
 * The black-and-white look (2026-10-10): the picture in greys, for printing
 * with black ink only - an invitation for a whole class need not empty the
 * colour cartridges. Made here from the preview copy, in a moment; kept beside
 * the photograph's own copies, the same size and shape.
 */
export async function blackWhiteOf(preview: Blob): Promise<PreparedFile[]> {
  const img = await decode(preview);
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = drawScaled(img, w, h, w, h);
  const g = c.getContext('2d')!;
  const data = g.getImageData(0, 0, w, h);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    // the eye's own weighting of red, green and blue
    const y = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
    px[i] = px[i + 1] = px[i + 2] = y;
  }
  g.putImageData(data, 0, 0);
  const out: PreparedFile[] = [];
  for (const kind of ['thumb', 'preview'] as SizeKind[]) {
    const s = fit(w, h, SIZES[kind]);
    const src = s.width === w && s.height === h ? c : drawScaled(c, w, h, s.width, s.height);
    out.push({ kind, blob: await toJpeg(src), ...s });
  }
  c.width = 0;
  return out;
}
